import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LocationSearchingOutlined, SearchOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Estate_FieldsFragment,
  EstateTaskIdentifier,
  EstateTaskInstance,
  useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import Spinner from 'common/spinner';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import EstateTaskInstancePaymentForm from 'pages/estate-checklist/components/estate-task/estate-task-instance-payment-form';
import EstateTrusuiteSearchPaymentForm from 'pages/estate-checklist/components/estate-trusuite-search-payment-form';
import { canAssignTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateBalanceSheetDropdownMenu from './estate-balance-sheet-dropdown-menu';

const useStyles = makeStyles(() => ({
  container: {
    width: '300px',
  },
  description: {
    fontSize: '14px',
    fontWeight: 300,
  },
}));

type Props = {
  estate: Estate_FieldsFragment;
};

const EstateBalanceSheetFindAccountsButton = ({ estate }: Props) => {
  const { portalId } = useParams<Record<string, string>>();
  const classes = useStyles();
  const [showTrusuiteSearchPaymentModal, setShowTrusuiteSearchPaymentModal] = useState(false);
  const [estateTaskInstanceCurrentlyEditing, setEstateTaskInstanceCurrentlyEditing] = useState<
    EstateTaskInstance | undefined
  >(undefined);
  const [showDelegatePaymentModal, setShowDelegatePaymentModal] = useState(false);

  const {
    data: estateTaskInstancesData,
    loading: loadingEstateTaskInstancesData,
    refetch: refetchEstateTaskInstanceData,
  } = useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { input: { estateId: estate.id, portalId, filter: { isArchived: false } } },
    onError: (e) => {
      showAlert({
        message: 'Error getting all estate task instances by estate ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleAssignClick = useCallback(
    (estateTaskInstance: EstateTaskInstance) => {
      if (!canAssignTasks(featureSubscriptionTypeData!.data)) {
        showToast({
          message: 'Please upgrade your account to push this task to Trustate.',
          severity: NotificationSeverity.WARNING,
        });
        return;
      }

      setEstateTaskInstanceCurrentlyEditing(estateTaskInstance);
      setShowDelegatePaymentModal(true);
    },
    [featureSubscriptionTypeData],
  );

  if (
    loadingEstateTaskInstancesData ||
    !estateTaskInstancesData ||
    featureSubscriptionTypeLoading
  ) {
    return <Spinner />;
  }

  const estateTaskInstances = estateTaskInstancesData.data || [];
  const trusuiteSearchTaskInstance = estateTaskInstances.find(
    (estateTaskInstance) => estateTaskInstance.identifier === EstateTaskIdentifier.TrusuiteSearch,
  );
  const searchTaskInstances = estateTaskInstances.filter(
    (estateTaskInstance) =>
      estateTaskInstance.isSearch &&
      estateTaskInstance.identifier !== EstateTaskIdentifier.TrusuiteSearch,
  );

  const options = [
    {
      title: 'Trusuite',
      description:
        trusuiteSearchTaskInstance?.labelHint ||
        'Use Trustate to run a search to receive decedent real property, liability, employment, & identity information, nearly instantly.',
      icon: <LocationSearchingOutlined />,
      taskInstance: trusuiteSearchTaskInstance,
      onClick: () => setShowTrusuiteSearchPaymentModal(true),
    },
    ...searchTaskInstances.map((taskInstance) => ({
      title: taskInstance.shortName || taskInstance.label,
      description: taskInstance.labelHint,
      icon: <LocationSearchingOutlined />,
      taskInstance,
      onClick: () => handleAssignClick(taskInstance as EstateTaskInstance),
    })),
  ];

  return (
    <>
      <CreatePaymentModal
        paymentForm={
          <EstateTrusuiteSearchPaymentForm
            setShowModal={setShowTrusuiteSearchPaymentModal}
            estate={estate}
          />
        }
        setShowModal={setShowTrusuiteSearchPaymentModal}
        showModal={showTrusuiteSearchPaymentModal}
      />
      <CreatePaymentModal
        paymentForm={
          <EstateTaskInstancePaymentForm
            estateTaskInstance={estateTaskInstanceCurrentlyEditing}
            setShowModal={setShowDelegatePaymentModal}
            replaceEstateTaskInstance={() => refetchEstateTaskInstanceData()}
            addEstateTaskInstance={() => refetchEstateTaskInstanceData()}
          />
        }
        setShowModal={setShowDelegatePaymentModal}
        showModal={showDelegatePaymentModal}
      />
      <EstateBalanceSheetDropdownMenu
        title="Find Accounts"
        startIcon={<SearchOutlined />}
        menuItems={options.map((option) => {
          return {
            content: (
              <Box className={classes.container} key={option.title} mb={2}>
                <Box display="flex">
                  {option.icon} <Typography ml={2}>{option.title}</Typography>
                </Box>
                <Typography
                  ml={5}
                  noWrap={false}
                  className={classes.description}
                  dangerouslySetInnerHTML={{
                    __html: `<span>${option.description}</span>`,
                  }}
                />
              </Box>
            ),
            onClick: option.onClick,
            disabled: !option.taskInstance,
          };
        })}
      />
    </>
  );
};

export default EstateBalanceSheetFindAccountsButton;
